import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  MantineProvider,
  createTheme,
  type MantineColorsTuple,
} from "@mantine/core";

/**
 * ONE colour scheme, wearing our tokens.
 *
 * Mantine brings its own theming, and the failure mode of adopting a component
 * library is ending up with two palettes that drift: the library's charts in
 * its blue, our panels in the brand purple, and a light mode that only half
 * the page respects. So the scheme lives HERE, in one place, and both systems
 * read it:
 *
 *   * `data-theme` on the root drives `styles.css`, which is unchanged.
 *   * `forceColorScheme` drives Mantine, which is told rather than asked.
 *
 * The brand ramp is the gradient's own stops. Mantine wants ten shades and
 * uses index 6 as the default filled colour, so the validated series colour
 * sits at 6 and the rest are stepped around it for hover, borders and
 * backgrounds. Those steps are chrome; the one that carries data is 6.
 */

const BRAND: MantineColorsTuple = [
  "#f2effd",
  "#e0d9fa",
  "#c1b1f4",
  "#a086ee",
  "#8562e9",
  // 5 and 6 are the two validated series-1 stops: light mode's #6C5CE7 and
  // dark mode's #8B5CF6. Mantine picks 6 for filled surfaces in dark and 6
  // for light too, which is why both live adjacent rather than at the ends.
  "#7a4ce6",
  "#8b5cf6",
  "#6c5ce7",
  "#5b47c9",
  "#4a38a8",
];

const TEAL: MantineColorsTuple = [
  "#e6f7fa",
  "#cbecf3",
  "#99d8e8",
  "#63c3dc",
  "#3ab2d3",
  "#22a8ce",
  "#17a2bd",
  "#0098b8",
  "#00819c",
  "#006c83",
];

const theme = createTheme({
  primaryColor: "brand",
  colors: { brand: BRAND, teal: TEAL },
  primaryShade: { light: 7, dark: 6 },
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  defaultRadius: "md",
  /**
   * Mantine's own surfaces are pointed at our tokens rather than its greys, so
   * a popover it renders and a panel we render are the same colour in both
   * modes and stay that way when the tokens change.
   */
  other: {},
  components: {
    Popover: {
      styles: {
        dropdown: {
          background: "var(--surface-1)",
          borderColor: "var(--border-strong)",
          color: "var(--text-primary)",
        },
      },
    },
    Tooltip: {
      styles: {
        tooltip: {
          background: "var(--surface-1)",
          border: "1px solid var(--border)",
          color: "var(--text-primary)",
        },
      },
    },
  },
});

type Scheme = "dark" | "light";

const SchemeContext = createContext<{
  scheme: Scheme;
  toggle: () => void;
}>({ scheme: "dark", toggle: () => undefined });

/** Read once, before first paint, so a light-mode user gets no dark flash. */
function initialScheme(): Scheme {
  return localStorage.getItem("neulytics-theme") === "light" ? "light" : "dark";
}

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [scheme, setScheme] = useState<Scheme>(initialScheme);

  useEffect(() => {
    document.documentElement.dataset["theme"] = scheme;
    localStorage.setItem("neulytics-theme", scheme);
  }, [scheme]);

  const toggle = useCallback(
    () => setScheme((was) => (was === "dark" ? "light" : "dark")),
    [],
  );
  const value = useMemo(() => ({ scheme, toggle }), [scheme, toggle]);

  return (
    <SchemeContext.Provider value={value}>
      <MantineProvider theme={theme} forceColorScheme={scheme}>
        {children}
      </MantineProvider>
    </SchemeContext.Provider>
  );
}

export function useScheme(): { scheme: Scheme; toggle: () => void } {
  return useContext(SchemeContext);
}
